import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type StaffSession,
  staffLoginSchema,
  staffLogoutSchema,
  toStaffSession,
  useStaffTokenStore,
} from '@/entities/staff-session';
import { type AppError, request, staffKeys } from '@/shared/api';

export interface StaffCredentials {
  readonly email: string;
  readonly password: string;
}

/**
 * Staff sign in with a password on their own guard (CLAUDE.md §3.3). A wrong
 * email and a wrong password come back as the same `identity.invalid_credentials`
 * — the panel must never help someone enumerate staff accounts (§3.8).
 */
export function useStaffLogin(): UseMutationResult<StaffSession, AppError, StaffCredentials> {
  const queryClient = useQueryClient();
  const setToken = useStaffTokenStore((state) => state.setToken);

  return useMutation<StaffSession, AppError, StaffCredentials>({
    mutationFn: async (credentials): Promise<StaffSession> =>
      toStaffSession(
        await request('/staff/auth/login', {
          method: 'POST',
          body: credentials,
          schema: staffLoginSchema,
        }),
      ),
    onSuccess: (session): void => {
      setToken(session.token);
      // the login response already carries the user, so `/staff/me` does not
      // need a round trip to know who just signed in
      queryClient.setQueryData(staffKeys.me(), session.user);
    },
  });
}

export function useStaffLogout(): UseMutationResult<{ revoked: boolean }, AppError, undefined> {
  const queryClient = useQueryClient();
  const setToken = useStaffTokenStore((state) => state.setToken);

  return useMutation<{ revoked: boolean }, AppError, undefined>({
    mutationFn: async (): Promise<{ revoked: boolean }> =>
      request('/staff/auth/logout', { method: 'POST', schema: staffLogoutSchema }),
    // a failed revoke still ends the local session: leaving a staff token in a
    // shared clinic browser is the worse outcome
    onSettled: (): void => {
      setToken(null);
      queryClient.clear();
    },
  });
}
